/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";
import geolayer from "@apis/geolayer";
import { svgIcon } from "@/components/Leaflet/Map/Marker/SvgIcon";
import SitemapIconDefault from "@images/default/sitemap_icon_default.svg";
import {
  PruneCluster,
  PruneClusterForLeaflet,
} from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import { ClusterIcon } from "@/components/Leaflet/Map/Marker/ClusterIcon";

interface SitemapImageData {
  topLeft?: { lat: number; lng: number };
  topRight?: { lat: number; lng: number };
  bottomLeft?: { lat: number; lng: number };
}

interface SitemapData {
  id: number;
  name: string;
  type?: { name?: string } | string;
  data?: SitemapImageData;
}

interface SitemapLayerOptions {
  ignoreSitemapId?: number;
}

const SitemapLayerClass = (L.FeatureGroup as any).extend({
  options: {},
  bounds: L.latLngBounds([]),
  pruneCluster: null as any,

  updateData: function (orgId: number | null, options?: SitemapLayerOptions) {
    const self = this;

    // Initialize PruneCluster if not already done
    if (!self.pruneCluster) {
      self.pruneCluster = new PruneClusterForLeaflet(120, 20);

      // Set cluster to use shadowPane for lower z-index
      self.pruneCluster.options.pane = "shadowPane";

      PruneCluster.Cluster.ENABLE_MARKERS_LIST = true;

      self.pruneCluster.BuildLeafletClusterIcon = function (cluster: any) {
        const iconClassName = "cluster-svg-sitemap";
        const markers = cluster.GetClusterMarkers();
        const _length = markers.length;

        let width = 24,
          textSize = 11;
        if (_length >= 500 && _length < 5000) {
          width = 36;
          textSize = 7;
        } else if (_length >= 5000) {
          width = 44;
          textSize = 6;
        }

        // White hexagon with dark text for sitemap cluster
        const svgString = `<svg width="${width}" height="${width}" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path id="cluster-bg" fill-rule="evenodd" clip-rule="evenodd" d="M11 0L21.7999 6V18L11 24L0.199951 18V6L11 0Z" fill="#FFFFFF" stroke="#212851" stroke-width="1"></path>
            <text id="cluster-text" x="50%" y="50%" font-size="${textSize}" font-weight="bold" transform="translate(0, 3.5)" text-anchor="middle" fill="#212851">${_length}</text>
            </svg>
        `;
        return ClusterIcon({
          count: _length,
          svgString: svgString,
          className: iconClassName,
        }) as any;
      };

      self.pruneCluster.PrepareLeafletMarker = function (leafletMarker: any, data: any) {
        leafletMarker.setIcon(data.icon);

        // Set marker to use shadowPane for lower z-index
        leafletMarker.options.pane = "shadowPane";

        if (data.tooltip) {
          leafletMarker.unbindTooltip();
          leafletMarker.bindTooltip(data.tooltip, {
            permanent: true,
            direction: "top",
            className: "transparent-tooltip",
            offset: [0, -8],
          });
        }
        leafletMarker.sitemapInfo = data.sitemapInfo;
      };

      // Add cluster layer to map if map is available
      if (self._map) {
        self._map.addLayer(self.pruneCluster);
      }
    }

    // Fetch site maps (IMAGE type geolayers)
    geolayer
      .getAllLayersForMapVisibility({
        orgId: orgId,
      })
      .then(
        ({ data }: any) => {
          self.clearLayers();

          // Clear existing markers from cluster
          self.pruneCluster.RemoveMarkers();

          // Filter only IMAGE type (site maps)
          const sitemaps: SitemapData[] = (data.data || []).filter(
            (layer: SitemapData) => {
              const typeName = typeof layer.type === "object" ? layer.type?.name : layer.type;
              return typeName === "IMAGE";
            }
          );

          for (let i = 0; i < sitemaps.length; i++) {
            const sitemap = sitemaps[i];

            if (options && options.ignoreSitemapId != null && +options.ignoreSitemapId === sitemap.id) {
              continue;
            }

            // Get center point from the image data
            const centerPoint = self.calculateCenterPoint(sitemap.data);

            if (centerPoint) {
              // Extend bounds
              self.bounds.extend([centerPoint[0], centerPoint[1]]);

              // Create sitemap icon marker and add to cluster
              const icon = svgIcon({
                iconUrl: SitemapIconDefault,
                id: String(sitemap.id),
                state: "sitemap",
                iconAnchor: [10, 30],
              });

              const marker = new PruneCluster.Marker(
                centerPoint[0],
                centerPoint[1],
                {
                  icon: icon,
                  tooltip: sitemap.name,
                }
              );
              marker.data.sitemapInfo = sitemap;
              (marker as any).getLatLng = function () {
                return new L.LatLng(centerPoint[0], centerPoint[1]);
              };

              self.pruneCluster.RegisterMarker(marker);
            }
          }

          // Process the cluster view
          self.pruneCluster.ProcessView();
        },
        (err: unknown) => {
          console.error("Error loading sitemap data:", err);
        }
      );
  },

  calculateCenterPoint: function (
    imageData?: SitemapImageData
  ): [number, number] | null {
    if (!imageData) return null;

    // IMAGE layer data has topLeft, topRight, bottomLeft coordinates
    const topLeft = imageData.topLeft;
    const topRight = imageData.topRight;
    const bottomLeft = imageData.bottomLeft;

    if (!topLeft || !topRight || !bottomLeft) return null;

    // Calculate center using diagonal: bottomRight = topRight + (bottomLeft - topLeft)
    const bottomRightLat = topRight.lat + (bottomLeft.lat - topLeft.lat);
    const bottomRightLng = topRight.lng + (bottomLeft.lng - topLeft.lng);

    const centerLat = (topLeft.lat + bottomRightLat) / 2;
    const centerLng = (topLeft.lng + bottomRightLng) / 2;

    return [centerLat, centerLng];
  },

  onAdd: function (map: L.Map) {
    this._map = map;
    if (this.pruneCluster) {
      map.addLayer(this.pruneCluster);
    }
    (L.FeatureGroup.prototype as any).onAdd.call(this, map);
  },

  onRemove: function (map: L.Map) {
    this._map = null;
    if (this.pruneCluster) {
      map.removeLayer(this.pruneCluster);
    }
    (L.FeatureGroup.prototype as any).onRemove.call(this, map);
  },

  getLayer: function (): any {
    return this;
  },

  getBounds: function (): L.LatLngBounds {
    return this.bounds;
  },
});

(L as any).SitemapLayer = SitemapLayerClass;
(L as any).sitemapLayer = function (opts?: L.LayerOptions): any {
  return new SitemapLayerClass(opts);
};

export const SitemapLayer = (L as any).sitemapLayer;
